import { useEffect, useState, useCallback } from "react";
import UserFleetList from "./UserFleetList";
import axiosLLInstance from "../../utils/axiosLLInstance";
import { useNavigate, useParams } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_USER_FLEETS,
  SET_USER_NEW_FLEETS,
} from "../../redux/actions/usersAction";
import BreadcrumbItems from "../../components/BreadcrumbItems";
import { Pagination, AutoComplete, Input } from "antd";
import { search } from "../Dashboard/icons";
import { PageSizeOptions, Role } from "../../utils/constants";
import debounce from "lodash.debounce"; // 👈 install if needed: npm i lodash.debounce

export default function UserFleets() {
  const { fleetUser } = useSelector((state) => state.users);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();

  const [fleets, setFleets] = useState([]);
  const [filteredFleets, setFilteredFleets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotalFleets] = useState(0);
  const [fleetsPerPage, setFleetsPerPage] = useState(PageSizeOptions.pageSize);
  const [searchQuery, setSearchQuery] = useState("");

  const getFleets = async (page = 1, limit = fleetsPerPage, query = "") => {
    try {
      const { data } = await axiosLLInstance.get(`/fleets/users/${userId}`, {
        params: {
          page,
          limit,
          q: query.trim() || undefined, // 👈 optional search param
        },
      });

      const { fleets, total } = data;
      dispatch(SET_USER_FLEETS(fleets));
      dispatch(SET_USER_NEW_FLEETS(fleets));
      setFleets(fleets);
      setFilteredFleets(fleets);
      setTotalFleets(total);
    } catch (error) {
      console.error("Error fetching fleets ", error);
    }
  };

  useEffect(() => {
    getFleets(currentPage, fleetsPerPage, searchQuery);
  }, [currentPage, fleetsPerPage]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setCurrentPage(1);
      getFleets(1, fleetsPerPage, value);
    }, 500),
    [fleetsPerPage]
  );

  const handleChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const onSizeChange = (current, pageSize) => {
    setCurrentPage(current);
    setFleetsPerPage(pageSize);
  };

  const onSelect = (fleet) => {
    const selectedFleet = JSON.parse(fleet);
    navigate(`/fleets/${selectedFleet.id}/devices`);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <BreadcrumbItems
          items={[
            {
              title: fleetUser?.name || `Manage Users`,
              to: `/users`,
            },
            { title: "Fleets" },
          ]}
        />

        <div className="search-adminBox flex items-center justify-between gap-4">
          <AutoComplete
            style={{ width: 200 }}
            onChange={handleChange}
            value={searchQuery}
            onSelect={onSelect}
            className="h-12 w-full border-gray-200 hover:border-gray-200 focus:border-gray-200 focus-within:border-gray-200 focus-within:shadow-none"
          >
            <Input
              size="large"
              className="h-12 border-gray-200 hover:border-gray-200 focus:border-gray-200 focus-within:border-gray-200 focus-within:shadow-none"
              placeholder="Search Fleet"
              prefix={<img src={search} alt="search icon" />}
            />
          </AutoComplete>

          {(auth.role === Role.SUPERADMIN || auth.role === Role.ADMIN) && (
            <div className="adminBtn">
              <button
                onClick={() => navigate(`/users/${userId}/assign-fleets`)}
                className="btn btn-neutral font-bold py-2 px-4 rounded-[10px] flex items-center justify-between text-[14px]"
              >
                Add Fleet <FaPlus className="pl-1 text-[18px]" />
              </button>
            </div>
          )}
        </div>
      </div>

      <UserFleetList fleets={filteredFleets} userId={userId} />

      {total ? (
        <div className="flex justify-end mt-4">
          <Pagination
            current={currentPage}
            total={total}
            pageSize={fleetsPerPage}
            onShowSizeChange={onSizeChange}
            onChange={paginate}
            showSizeChanger={true}
            pageSizeOptions={PageSizeOptions.options}
          />
        </div>
      ) : null}
    </>
  );
}
